import React, { useState } from "react";
import { CardContainer } from "./MemberDirectorySettings";
import StyledText from "../../components/StyledComponents/StyledText";
import StyledInput from "../../components/StyledComponents/StyledInput";
import { Button } from "../../components/form/Button";
import CrossIcon from "../../assets/icons/CrossIcon";
import SaveIcon from "../../assets/icons/SaveIcon";
import { useChangePasswordMutation } from "../../api/accountApiSlice";
import { Field, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import FormikInputField from "../../components/Formik/FormikInputField";
import { useToast } from "../../components/toast/ToastContext";

interface ChangePasswordValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// 🔹 Validation schema with Yup
const validationSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(8, "New password must be at least 8 characters"),
  confirmPassword: Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref("newPassword"), ""], "Passwords do not match"),
});

const ChangePassword = () => {
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");
  const showToast = useToast();
  const [changePassword, { isLoading: isMutating }] =
    useChangePasswordMutation();

  // 🔹 Initial form values
  const initialValues: ChangePasswordValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  // 🔹 Form submit handler with FormikHelpers
  const handleSubmit = async (
    values: ChangePasswordValues,
    {
      setSubmitting,
      resetForm,
      setFieldError,
    }: FormikHelpers<ChangePasswordValues>,
  ) => {
    setApiError("");
    setApiSuccess("");

    const payload = {
      previousPassword: values.currentPassword,
      proposedPassword: values.newPassword,
    };

    try {
      await changePassword(payload).unwrap();
      setApiSuccess("Password updated successfully!");
      resetForm();
      showToast("Success", "Password Updated successfully", "success");
    } catch (err: any) {
      const errorMessage =
        err?.data?.message || err?.error || "Failed to update password";

      showToast("Error", errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CardContainer>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          isSubmitting,
          handleChange,
          handleReset,
          dirty,
          isValid,
        }) => (
          <Form>
            <div className="border-bottom mb-3 pb-3">
              <StyledText
                fontSize="15px"
                fontWeight={700}
                textColor={"var(--app-primary-color)"}
              >
                Change Password
              </StyledText>
            </div>
            {apiError && (
              <StyledText
                fontSize="13px"
                textColor="var(--app-error-color, #dc3545)"
                className="mb-3"
                style={{ display: "block" }}
              >
                {apiError}
              </StyledText>
            )}
            {apiSuccess && (
              <StyledText
                fontSize="13px"
                textColor="var(--app-success-color, #28a745)"
                className="mb-3"
                style={{ display: "block" }}
              >
                {apiSuccess}
              </StyledText>
            )}
            <StyledText
              className="mt-4"
              fontSize="14px"
              fontWeight={600}
              textColor={"var(--app-secondary-color)"}
              style={{ marginBottom: "10px" }}
            >
              Current Password
            </StyledText>
            <FormikInputField
              name="currentPassword"
              type="password"
              placeholder="Current Password"
              disabled={isMutating}
              onChange={(e) => {
                handleChange(e);
                // if (apiError) setApiError('')
              }}
              // isShowError
            />
            <StyledText
              className="mt-4"
              fontSize="14px"
              fontWeight={600}
              textColor={"var(--app-secondary-color)"}
              style={{ marginBottom: "10px" }}
            >
              New Password
            </StyledText>
            <FormikInputField
              name="newPassword"
              placeholder="New Password"
              type="password"
              disabled={isMutating}
              onChange={(e) => {
                handleChange(e);
              }}
              // isShowError
            />
            <StyledText
              className="mt-4"
              fontSize="14px"
              fontWeight={600}
              textColor={"var(--app-secondary-color)"}
              style={{ marginBottom: "10px" }}
            >
              Confirm Password
            </StyledText>
            <FormikInputField
              name="confirmPassword"
              placeholder="Confirm Password"
              type="password"
              disabled={isMutating}
              onChange={(e) => {
                handleChange(e);
              }}
              // isShowError
            />
            {dirty && (
              <div className="d-flex justify-content-end gap-2 mt-4 pb-2">
                <Button
                  type="button"
                  btnBg="#ffffff"
                  color="rgba(88, 85, 88, 1)"
                  borderColor="rgba(186, 189, 193, 1)"
                  $variant="customButton"
                  onClick={() => {
                    handleReset();
                    setApiError("");
                    setApiSuccess("");
                  }}
                  disabled={isMutating}
                >
                  <CrossIcon size={20} /> Close
                </Button>
                <Button
                  type="submit"
                  btnBg={"var(--app-primary-color)"}
                  color={"var(--app-text-color)"}
                  // borderColor='rgba(186, 189, 193, 1)'
                  $variant="customButton"
                  disabled={isMutating || isSubmitting}
                  style={{ opacity: isMutating || isSubmitting ? 0.7 : 1 }}
                >
                  {isMutating || isSubmitting ? (
                    <span>Updating...</span>
                  ) : (
                    <>
                      <SaveIcon size={20} color={"var(--app-text-color)"} />{" "}
                      Update Password
                    </>
                  )}
                </Button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </CardContainer>
  );
};

export default ChangePassword;
